import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import {useTranslation} from 'react-i18next'
import type {ImageFormat} from '../../../common/constants/converterFormats'
import {cropImage, FULL_CROP_RECT, type CropRect} from '../../../common/utils/imageCrop'
import {useConverterImageUpload} from './useConverterImageUpload'
import ImageCropOverlay from './ImageCropOverlay'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {reportError} from '../../../common/errors/reportError'

interface ImagePreviewCanvasProps {
  image: HTMLImageElement | null
  format: ImageFormat
  quality: number
  onImageLoaded: (image: HTMLImageElement, isAnimatedGif: boolean) => void
  onCropApplied: (image: HTMLImageElement) => void
}

// JPEG and BMP have no alpha channel — the download itself flattens onto white
// (imageConverter.ts), so the live preview does the same for visual parity.
const FORMATS_WITHOUT_ALPHA = new Set<ImageFormat>(['jpeg', 'bmp'])
// Formats whose export quality is worth simulating live (round-trip through a
// real encode) rather than just drawing the raw source.
const QUALITY_PREVIEW_FORMATS = new Set<ImageFormat>(['jpeg', 'webp'])
const QUALITY_DEBOUNCE_MS = 120

const ImagePreviewCanvas = ({image, format, quality, onImageLoaded, onCropApplied}: ImagePreviewCanvasProps) => {
  const {t} = useTranslation()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const {busy: uploadBusy, handleFileSelected} = useConverterImageUpload(image !== null, onImageLoaded)
  const [dragOver, setDragOver] = useState(false)
  const [cropMode, setCropMode] = useState(false)
  const [cropRect, setCropRect] = useState<CropRect>(FULL_CROP_RECT)
  const [cropBusy, setCropBusy] = useState(false)

  // Live render. For JPEG/WebP this round-trips the source through a real
  // canvas.toBlob() encode at the current quality and draws the DECODED result —
  // so the preview shows actual compression artifacts, not just a flat draw that
  // happens to ignore the quality slider entirely.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !image) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const w = image.naturalWidth || image.width
    const h = image.naturalHeight || image.height
    canvas.width = w
    canvas.height = h

    const drawFlat = () => {
      ctx.clearRect(0, 0, w, h)
      if (FORMATS_WITHOUT_ALPHA.has(format)) {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, w, h)
      }
      ctx.drawImage(image, 0, 0)
    }

    if (!QUALITY_PREVIEW_FORMATS.has(format)) {
      drawFlat()
      return
    }

    let cancelled = false
    const timer = window.setTimeout(() => {
      const source = document.createElement('canvas')
      source.width = w
      source.height = h
      const sctx = source.getContext('2d')
      if (!sctx) return
      if (FORMATS_WITHOUT_ALPHA.has(format)) {
        sctx.fillStyle = '#ffffff'
        sctx.fillRect(0, 0, w, h)
      }
      sctx.drawImage(image, 0, 0)

      const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/webp'
      source.toBlob(
        (blob) => {
          if (cancelled || !blob) return
          createImageBitmap(blob)
            .then((bitmap) => {
              if (cancelled) return
              ctx.clearRect(0, 0, w, h)
              ctx.drawImage(bitmap, 0, 0)
              bitmap.close()
            })
            .catch(() => {
              if (!cancelled) drawFlat()
            })
        },
        mimeType,
        quality,
      )
    }, QUALITY_DEBOUNCE_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [image, format, quality])

  const handleContextMenu = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    e.preventDefault()
  }, [])

  const handleDrop = useCallback(
    (e: ReactDragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files?.[0]
      if (file) void handleFileSelected(file)
    },
    [handleFileSelected],
  )
  const handleDragOver = useCallback((e: ReactDragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(true)
  }, [])
  const handleDragLeave = useCallback(() => setDragOver(false), [])

  const handleToggleCrop = useCallback(() => {
    setCropRect(FULL_CROP_RECT)
    setCropMode(true)
  }, [])

  const handleCancelCrop = useCallback(() => {
    setCropMode(false)
    setCropRect(FULL_CROP_RECT)
  }, [])

  const handleApplyCrop = useCallback(async () => {
    if (!image) return
    setCropBusy(true)
    try {
      const cropped = await cropImage(image, cropRect)
      onCropApplied(cropped)
      trackEvent('image_converter_crop_applied')
      setCropMode(false)
      setCropRect(FULL_CROP_RECT)
    } catch (err) {
      reportError(err, 'image-converter-crop')
    } finally {
      setCropBusy(false)
    }
  }, [image, cropRect, onCropApplied])

  if (!image) {
    return (
      <div className="imc-preview-stage">
        <div
          className={dragOver ? 'imc-preview-drop is-dragover' : 'imc-preview-drop'}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <p className="cvt-hint">{t('imageConverter.preview.dropHint')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="imc-preview-stage">
      <div
        className={dragOver ? 'imc-preview-canvas-wrap is-dragover' : 'imc-preview-canvas-wrap'}
        onContextMenu={handleContextMenu}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <canvas ref={canvasRef} className="imc-preview-canvas" />
        {cropMode && <ImageCropOverlay rect={cropRect} onRectChange={setCropRect} />}
      </div>

      <div className="imc-preview-toolbar">
        {!cropMode ? (
          <button type="button" className="cvt-btn cvt-btn-secondary" onClick={handleToggleCrop}>
            {t('imageConverter.preview.cropStart')}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="cvt-btn cvt-btn-primary"
              onClick={() => void handleApplyCrop()}
              disabled={cropBusy}
            >
              {cropBusy ? t('imageConverter.preview.cropApplying') : t('imageConverter.preview.cropApply')}
            </button>
            <button type="button" className="cvt-btn cvt-btn-ghost" onClick={handleCancelCrop} disabled={cropBusy}>
              {t('imageConverter.preview.cropCancel')}
            </button>
          </>
        )}
      </div>
      {uploadBusy && <p className="cvt-hint">{t('imageConverter.upload.busy')}</p>}
    </div>
  )
}

export default ImagePreviewCanvas
